import { FC } from 'react';

import { Button, Tooltip } from 'antd';

import {
    IUtmCabinetScheme,
    IUtmSchemesSummary,
    isUtmSchemesSource,
    normalizeSchemesSummary,
    pluralizeValues,
} from './types';
import { formatNumberWithSpaces } from '../../../../../helpers/utils/functions';

/**
 * Название схемы («Яндекс.Директ, чередование (utm_campaign)») переносится по словам.
 * Высота строки таблицы кабинетов не меньше 84px: три строки названия плюс строка
 * с подписью и ссылкой в неё укладываются, поэтому обрезка практически не наступает.
 */
const NAME_STYLE = {
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical' as const,
    maxWidth: '100%',
    overflow: 'hidden',
    overflowWrap: 'anywhere' as const,
    lineHeight: 1.25,
} as const;

/** Серая подпись под названием: считанные значения и пометка «выключена» */
const NOTE_STYLE = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: '#8c8c8c',
    fontSize: 12,
} as const;

interface ISchemesCellProps {
    /** Строка таблицы кабинетов */
    rowData: {
        id?: string | number;
        source_id?: number;
        ad_account?: string;
        /** Сводка схемы кабинета от бэкенда либо свежая сводка, подмешанная таблицей */
        utm_scheme?: IUtmCabinetScheme | IUtmSchemesSummary;
    };
    /** Открыть окно настройки схемы кабинета */
    onOpen: (cabinet: { id: number; name: string }) => void;
}

/**
 * Ячейка столбца «Настройка параметров в ссылке».
 *
 * У кабинета одна схема разбора. Не настроена — кнопка «Настроить». Настроена — название
 * схемы, число разобранных значений и кнопка «Изменить»; выключенная схема помечена.
 * У источников без упакованных меток — прочерк.
 */
const SchemesCell: FC<ISchemesCellProps> = ({ rowData, onOpen }) => {
    if (!isUtmSchemesSource(rowData?.source_id)) {
        return <span style={{ color: '#bfbfbf' }}>—</span>;
    }

    const summary = normalizeSchemesSummary(rowData?.utm_scheme);
    // В строке таблицы id приходит строкой, в /utm-schemes ожидается число
    const cabinetId = Number(rowData?.id);
    const cabinetName = rowData?.ad_account || `Кабинет ${cabinetId}`;
    const openSchemes = () => onOpen({ id: cabinetId, name: cabinetName });

    if (summary.count === 0) {
        return (
            <Button onClick={openSchemes}>
                Настроить
            </Button>
        );
    }

    const schemeName = summary.names[0] || 'Схема разбора';
    const isDisabled = summary.active === 0;

    return (
        // Ячейка таблицы — flex-контейнер, а у flex-элемента min-width: auto: без minWidth 0
        // длинное название схемы распирает блок и он вылезает на соседние столбцы
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                rowGap: 3,
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'hidden',
            }}
        >
            <Tooltip title={schemeName}>
                <span style={NAME_STYLE}>{schemeName}</span>
            </Tooltip>

            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    columnGap: 8,
                    width: '100%',
                    minWidth: 0,
                }}
            >
                <span style={NOTE_STYLE}>
                    {formatNumberWithSpaces(summary.parsed_values)}&nbsp;
                    {pluralizeValues(summary.parsed_values)}
                    {isDisabled && ' · выключена'}
                </span>
                <Button
                    type="link"
                    size="small"
                    onClick={openSchemes}
                    style={{ padding: 0, height: 'auto', flexShrink: 0 }}
                >
                    Изменить
                </Button>
            </div>
        </div>
    );
};

export default SchemesCell;
